<template>
  <div class="patients-block patients">
    <preloader :visible="preloader" />
    <v-server-table ref="vuetable" :url="route" :columns="columns" :options="options" @loading="onLoading" @loaded="onLoaded" class="custom-table-modifier">
      <template v-slot:created_at="d"><span v-html="utc(d.row.created_at).local().format(dateFormat + '[<br />]' + timeFormat)"></span></template>
      <template v-slot:name="d"><span>{{ d.row.last_name + ' ' + d.row.first_name }}</span></template>
      <template v-slot:device="d"><span>{{ d.row.monitoring_serial_number }}</span></template>
      <template v-slot:monitoring="d">
        <span>{{ stamp(d.row.monitoring_start_local).utc().format(dateFormat) + ' - ' + stamp(d.row.monitoring_finish_local).utc().format(dateFormat) }}</span>
      </template>
      <template v-slot:service="d">
        <div v-if="hasServices(d.row)" class="width-full">
          <div v-for="(e) in d.row.services" :key="e.id">{{ serviceDetails(e, d.row) }} (<a :href="`/service/${e.id}` + (serviceEdit ? '/edit' : '')">#{{ e.id }}</a>)</div>
        </div>
        <span v-else-if="hasCandidates(d.row)"><v-autocomplete :name="`service-${d.row.id}`" clearable no-data-text="Not assigned"
                                     :items="d.row.candidates" item-value="id" :item-text="item => serviceName(item)" @change="onChanged(d.row.id, ...arguments)">
        </v-autocomplete></span>
        <span v-else>&nbsp;</span>
      </template>
      <template v-slot:actions="d" class="actions-bts">
        <a v-if="hasCandidates(d.row)" href="#" class="btn btn-sm btn-secondary" @click="save(d.row.id, ...arguments)"><i class="icmn-floppy-disk" aria-hidden="true"></i></a>
        <a v-if="canEdit && d.row.id" :href="`/request/${d.row.id}/edit`" class="btn btn-sm btn-warning"><i class="icmn-pencil2" aria-hidden="true"></i></a>
        <a v-if="d.row.id" :href="'/request/' + d.row.id + '/view'" target="_blank" class="btn btn-sm btn-info"><i class="icmn-eye" aria-hidden="true"></i></a>
        <span v-if="canDelete && !hasServices(d.row)" class="padding-left-15">
          <a @click="tryDelete(d.row.id)" class="btn btn-sm btn-danger" href="#"><i class="icmn-bin" aria-hidden="true"></i></a>
        </span>
      </template>
    </v-server-table>
  </div>
</template>

<script>
import Preloader from './blocks/preloader';
import { CvService } from '../cv-service.js';

export default {
  components: { Preloader },
  props: {
    route: {
      type: String
    },
    dateFormat: {
      type: String,
      default: 'MM-DD-YYYY'
    },
    timeFormat: {
      type: String,
      default: 'h:m:s'
    },
    serviceEdit: {
      type: Boolean,
      default: false
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    canDelete: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      preloader: false,
      columns: [ 'created_at', 'name', 'device', 'monitoring', 'service', 'actions' ],
      options: {
        columnsClasses: {
          service: 'ignore-custom-table-modifier'
        },
        filterByColumn: true,
        debounce: 1000,
        requestFunction: (data) => {
          return axios.get(this.route, { params: data });
        },
        dateColumns:[ 'created_at' ],
        dateFormat: this.dateFormat,
        datepickerOptions: {
          showDropdowns: true,
          autoUpdateInput: true,
        },
        orderBy: {
          ascending: false,
        },
        sortable: [],
        filterable: [ 'name', 'device', 'created_at' ],
        perPage: 30,
        pagination: {
          chunk: 30,
          dropdown: false
        },
        headings: {
          name: 'Name',
          device: 'Device',
          monitoring: 'Monitoring',
          service: 'Service',
          created_at: 'Created',
          actions: 'Actions',
        },
      },

      changes: {}
    }
  },
  methods: {
    utc(stamp) {
      return moment.utc(stamp);
    },
    stamp(...args) {
      return moment(...args);
    },
    onLoading(e) {
      this.preloader = true;
    },
    onLoaded(e) {
      this.preloader = false;
    },

    serviceDetails(item, req) {
      item.request = req;
      return CvService.getServiceNameOrNotes(item);
    },
    serviceName: (item) => {
      return `#${item.id} - `+ (item.name ? `"${item.name}" - ` : '') + `${item.device.serial}`;
    },

    hasServices(row) {
      return row.services && Array.isArray(row.services) && row.services.length > 0;
    },
    hasCandidates(row) {
      return !this.hasServices(row) && Array.isArray(row.candidates) && row.candidates.length > 0;
    },
    onChanged(id, serviceId) {
      if (!serviceId) delete this.changes[id];
      else this.changes[id] = serviceId;
    },
    save(id, e) {
      e.preventDefault();

      const self = this;
      axios.post(`/request/${id}`, { service: this.changes[id] }).then(response => {
        if (response.data.error) {
          self.toast(response.data.error, 'error');
        } else {
          self.toast(response.data.success);
          this.$refs.vuetable.getData()
        }
      }).catch(e => {
        console.log(e);
        self.toast('Can not process item because of server error.', 'error');
      });
    },

    tryDelete(id) {
      this.$swal.fire({
        title: 'Are you sure?',
        text: 'You will not be able to recover this record!',
        icon: 'warning',
        confirmButtonText: 'Delete',
        showCloseButton: true,
        showCancelButton: true,
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteItem(id);
        }
      });
    },
    deleteItem(id) {
      const self = this;
      axios.delete(`/request/${id}`).then(response => {
        if (response.data.error) {
          self.toast(response.data.error, 'error');
        } else {
          self.toast(response.data.success);
          this.$refs.vuetable.getData()
        }
      }).catch(e => {
        console.log(e);
        self.toast('Can not process item because of server error.', 'error');
      });
    },

    toast(message, type) {
      const self = this.$swal;
      self.fire({
        text: message,
        icon: type || 'success',
        toast: true,
        showConfirmButton: false,
        position: 'top-end',
        timer: 1500,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', self.stopTimer)
          toast.addEventListener('mouseleave', self.resumeTimer)
        }
      });
    }
  }
}
</script>
